import { Settings, ShieldCheck, Mail, Zap, PenTool, AlertTriangle, Clock, Headphones, Star, ArrowRight } from "lucide-react";
import fixswiftLogo from "@/assets/fixswift-logo.svg";
import { useState, useEffect } from "react";

const MaintenanceMode = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => (prev >= 78 ? 78 : prev + 1));
    }, 60);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden bg-[#0a0f1a] text-zinc-50 selection:bg-primary/30 font-body">
      
      {/* Background Orbs — CSS animations instead of framer-motion */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-[15%] -left-[15%] h-[50%] w-[70%] rounded-full bg-primary/15 blur-[150px] animate-float-slow" />
        <div className="absolute bottom-[5%] -right-[15%] h-[45%] w-[65%] rounded-full bg-blue-600/15 blur-[150px] animate-float-slow-reverse" />
        <div className="absolute top-[40%] left-[30%] h-[25%] w-[40%] rounded-full bg-amber-500/8 blur-[120px] animate-float-slow" />
      </div>

      {/* Grid + Noise */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.15] mix-blend-overlay" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />
      
      {/* Radial spotlight */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[600px] w-[800px] bg-[radial-gradient(ellipse_at_center,_rgba(23,128,93,0.12)_0%,_transparent_70%)]" />

      {/* Warning Ticker */}
      <div className="relative z-20 flex w-full overflow-hidden border-b border-red-500/20 bg-gradient-to-r from-red-500/10 via-red-600/15 to-red-500/10 py-2.5 text-[10px] font-bold uppercase tracking-[0.25em] text-red-400 backdrop-blur-xl">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, i) => (
             <div key={i} className="flex items-center">
               <span className="mx-5">System Upgrade In Progress</span>
               <AlertTriangle className="h-3 w-3" />
               <span className="mx-5">Please Stand By</span>
               <AlertTriangle className="h-3 w-3" />
               <span className="mx-5">Maintenance Ongoing</span>
               <AlertTriangle className="h-3 w-3" />
               <span className="mx-5">Upgrading Infrastructure</span>
               <AlertTriangle className="h-3 w-3" />
             </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 flex flex-1 flex-col px-5 sm:px-6 lg:px-8 max-w-2xl mx-auto w-full">
        
        {/* Header */}
        <header className="flex w-full items-center justify-between py-4 animate-fade-in">
          <div className="flex items-center gap-2.5">
            <img src={fixswiftLogo} alt="Fixswift Logo" className="h-10 w-10 rounded-xl shadow-lg shadow-primary/30 ring-1 ring-white/10" />
            <span className="font-heading text-xl font-bold tracking-tight text-white">Fixswift</span>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/10 px-3.5 py-1.5 text-[10px] font-semibold tracking-wider backdrop-blur-md uppercase">
            <div className="relative">
              <div className="h-2 w-2 rounded-full bg-amber-500" />
              <div className="absolute inset-0 h-2 w-2 animate-ping rounded-full bg-amber-500/60" />
            </div>
            <span className="text-amber-300">Under Maintenance</span>
          </div>
        </header>

        {/* Main Hero */}
        <main className="mt-6 sm:mt-10 flex flex-col items-center text-center flex-1">
          {/* Icon Hub */}
          <div className="relative mt-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="absolute -inset-8 rounded-full border border-dashed border-white/10 animate-spin-slow">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-primary/60" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 h-1.5 w-1.5 rounded-full bg-blue-400/60" />
            </div>
            <div className="absolute -inset-3 rounded-full border border-dashed border-white/5 animate-spin-slow-reverse">
              <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-amber-400/60" />
            </div>
            <div className="relative flex h-28 w-28 items-center justify-center rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/[0.02] shadow-2xl backdrop-blur-xl">
              <div className="absolute -right-3 -top-3 animate-spin-slow">
                <Settings className="h-8 w-8 text-primary/80 drop-shadow-[0_0_8px_rgba(23,128,93,0.4)]" />
              </div>
              <PenTool className="h-11 w-11 text-zinc-100 drop-shadow-md" />
              <div className="absolute -bottom-2 -left-2 flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-black shadow-[0_0_20px_rgba(245,158,11,0.5)] ring-2 ring-amber-400/20 animate-pulse">
                <Zap className="h-4.5 w-4.5" />
              </div>
            </div>
          </div>
          
          <h1 className="mt-12 font-heading text-4xl font-black tracking-tight text-white sm:text-5xl leading-[1.1] animate-fade-in" style={{ animationDelay: '0.3s' }}>
            Building the <br />
            <span className="bg-gradient-to-r from-primary via-emerald-400 to-green-300 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(23,128,93,0.3)]">Best Experience.</span>
          </h1>
          <p className="mt-4 max-w-[320px] text-sm leading-relaxed text-zinc-400 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            We are fine-tuning our platform to bring you India's ultimate destination for premium refurbished devices.
          </p>

          {/* Progress Bar */}
          <div className="mt-7 w-full max-w-xs animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Upgrade Progress</span>
              <span className="text-[11px] font-bold text-primary">{progress}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden border border-white/5">
              <div 
                className="h-full rounded-full bg-gradient-to-r from-primary via-emerald-400 to-green-400 shadow-[0_0_12px_rgba(23,128,93,0.5)] transition-[width] duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Terminal */}
          <div className="mt-7 w-full max-w-sm overflow-hidden rounded-2xl border border-white/10 bg-black/50 shadow-2xl backdrop-blur-md animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <div className="flex items-center border-b border-white/10 bg-white/[0.03] px-4 py-3">
              <div className="flex gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
                <div className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
                <div className="h-2.5 w-2.5 rounded-full bg-green-500/80" />
              </div>
              <span className="ml-3 font-mono text-[10px] font-medium tracking-wider text-zinc-500">fixswift_upgrade.sh</span>
              <div className="ml-auto flex items-center gap-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="font-mono text-[9px] text-green-500/70">RUNNING</span>
              </div>
            </div>
            <div className="p-4 text-left font-mono text-[11px] leading-7 text-zinc-400">
              <p className="text-zinc-600"># Initializing Fixswift Core v2.0</p>
              <div><span className="text-green-400">✓</span> Optimizing product catalog... <span className="text-green-400 font-semibold">[DONE]</span></div>
              <div><span className="text-green-400">✓</span> Enhancing 21-point checks... <span className="text-green-400 font-semibold">[DONE]</span></div>
              <div><span className="text-green-400">✓</span> Upgrading payment gateway... <span className="text-green-400 font-semibold">[DONE]</span></div>
              <div><span className="text-amber-400">⟳</span> Preparing massive discounts... <span className="animate-pulse text-amber-400/70">in progress</span></div>
            </div>
          </div>

          {/* ETA badge */}
          <div className="mt-5 flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 backdrop-blur-sm animate-fade-in" style={{ animationDelay: '0.8s' }}>
            <Clock className="h-3.5 w-3.5 text-zinc-400" />
            <span className="text-[11px] font-medium text-zinc-300">Expected back online in a few hours</span>
          </div>
        </main>

        {/* Bottom Section */}
        <div className="mt-auto pt-8 pb-5 animate-fade-in" style={{ animationDelay: '0.7s' }}>
          <div className="mb-4 flex items-center justify-center gap-3 opacity-60">
            <div className="h-[1px] flex-1 max-w-[40px] bg-gradient-to-r from-transparent to-zinc-600" />
            <p className="text-center text-[9px] font-bold uppercase tracking-[0.25em] text-zinc-400">While you wait</p>
            <div className="h-[1px] flex-1 max-w-[40px] bg-gradient-to-l from-transparent to-zinc-600" />
          </div>
          
          <div className="grid grid-cols-3 gap-2.5">
            {[
              { icon: ShieldCheck, label: "21-Point Check", desc: "Rigorous quality testing", gradient: "from-primary/15 to-primary/5", iconColor: "text-primary", iconGlow: "shadow-[0_0_12px_rgba(23,128,93,0.3)]" },
              { icon: Star, label: "Premium Fleet", desc: "Apple & Windows devices", gradient: "from-blue-500/15 to-blue-500/5", iconColor: "text-blue-400", iconGlow: "shadow-[0_0_12px_rgba(59,130,246,0.3)]" },
              { icon: Headphones, label: "24/7 Support", desc: "Always here for you", gradient: "from-amber-500/15 to-amber-500/5", iconColor: "text-amber-400", iconGlow: "shadow-[0_0_12px_rgba(245,158,11,0.3)]" },
            ].map((item, i) => (
              <div 
                key={i}
                className={`flex flex-col items-center text-center rounded-2xl border border-white/10 bg-gradient-to-b ${item.gradient} p-4 backdrop-blur-sm transition-all hover:bg-white/10 hover:border-white/20 hover:scale-[1.02]`}
              >
                <div className={`mb-2.5 flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 ${item.iconColor} ${item.iconGlow}`}>
                  <item.icon className="h-5 w-5" />
                </div>
                <h3 className="text-[11px] font-bold text-white tracking-tight leading-tight">{item.label}</h3>
                <p className="mt-1 text-[9px] leading-tight text-zinc-500">{item.desc}</p>
              </div>
            ))}
          </div>

          <a 
            href="mailto:support@fixswift.in" 
            className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-2xl border border-white/10 bg-gradient-to-r from-white/[0.06] to-white/[0.02] py-4 text-sm font-semibold text-white backdrop-blur-sm transition-all hover:from-white/10 hover:to-white/[0.05] hover:border-white/20 active:scale-[0.98] group"
          >
            <Mail className="h-4 w-4 text-zinc-400 group-hover:text-primary transition-colors" />
            Contact Support Team
            <ArrowRight className="h-3.5 w-3.5 text-zinc-500 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceMode;
